import {Undef} from 'app/interface';
import {parseURLQuery} from 'app/utils/location/parseURLQuery';
import {replacePlaceholdersInObject} from 'app/utils/replacePlaceholder/replacePlaceholders';
import {getDataFromStorage} from 'app/utils/storage/getDataFromStorage';
import {UrlQueryParams, Parameters} from 'app/utils/storage/types';
import {UrlParamsConfig} from 'interfaces/common/components/configuration/urlparams/urlParamsConfig';

import {GemSettings} from '../config/interface';

export function buildParams(saveAll: boolean, parameters: UrlParamsConfig[], gemSettings: GemSettings, storeURLFragment: Undef<boolean>): Parameters {
    const queryParams = getUrlQueryParams(parameters, saveAll || storeURLFragment);
    const sessionStorageParams = getDataFromStorage('SESSION');
    const paramsToHide: string[] = [];
    const defaultParams = parameters.reduce<Record<string, string | undefined>>((acc, urlParam) => {
        if (urlParam.removeFromUrl) {
            paramsToHide.push(urlParam.name);
        }

        if (urlParam.defaultValue) {
            return {
                ...acc,
                [urlParam.name]: getParameterDefaultValue(urlParam),
            };
        }

        return acc;
    }, {});

    const replacedDefaultParams = replacePlaceholdersInObject({obj: defaultParams, gemSettings});

    if (saveAll) {
        return {
            values: {...replacedDefaultParams, ...sessionStorageParams, ...queryParams},
            hide: paramsToHide,
        };
    }

    const selectedParams = parameters.reduce<UrlQueryParams>((acc, urlParam) => {
        const value = queryParams[urlParam.name];
        const valueBase64 = queryParams[base64ParamName(urlParam.name)];

        if (isSelectableParam(value)) {
            const selected = {...acc, [urlParam.name]: value};

            if (valueBase64) {
                selected[base64ParamName(urlParam.name)] = valueBase64;
            }

            return selected;
        }

        return acc;
    }, {});

    return {
        values: {...replacedDefaultParams, ...sessionStorageParams, ...selectedParams, ...getUrlFragmentParam(storeURLFragment, queryParams)},
        hide: paramsToHide,
    };
}

export function getUrlQueryParams(parameters: UrlParamsConfig[], storeURLFragment = false) {
    const searchLocationParams = parseURLQuery(window.location.search);
    const hashLocationParams = getHashLocationParams(window.location.hash, storeURLFragment);
    const locationParams = {...searchLocationParams, ...hashLocationParams};

    const urlQueryParams: UrlQueryParams = {...locationParams};

    parameters.forEach(({name, decodeBase64}) => {
        if (!decodeBase64) {
            return;
        }

        const rawValue = locationParams[name];
        if (!rawValue) {
            return;
        }

        urlQueryParams[base64ParamName(name)] = rawValue;

        try {
            const decoded = window.atob(rawValue);
            const decodedParsed = parseJson(decoded);
            if (decodedParsed) {
                urlQueryParams[name] = decodedParsed;
            } else {
                urlQueryParams[name] = decoded;
            }
        } catch (e) {
            console.log(e);
        }
    });

    return urlQueryParams;
}

function getParameterDefaultValue(parameter: UrlParamsConfig): string | undefined {
    if (!parameter.defaultValue || !parameter.decodeBase64) {
        return parameter.defaultValue;
    }

    const defaultValueParsed = parseJson(parameter.defaultValue);
    if (defaultValueParsed) {
        return defaultValueParsed;
    }
}

function parseJson(str: string) {
    try {
        return JSON.parse(str);
    } catch {
        return false;
    }
}

function isSelectableParam(value: UrlQueryParams[0]) {
    return typeof value === 'string' || (typeof value === 'object' && value !== null);
}

function base64ParamName(name: string) {
    return `${name}_base64`;
}

function getUrlFragmentParam(storeURLFragment: Undef<boolean>, queryParams: UrlQueryParams): Record<string, string> {
    if (storeURLFragment && typeof queryParams.urlFragment === 'string') {
        return {urlFragment: queryParams.urlFragment};
    }

    return {};
}

function getHashLocationParams(locationHash: string, storeURLFragment: boolean): Record<string, string> {
    if (!locationHash.startsWith('#')) {
        return {};
    }

    const parsedLocationHash = locationHash.substring(1);
    let hashLocationParams = {...parseURLQuery(parsedLocationHash)};

    if (storeURLFragment && parsedLocationHash) {
        hashLocationParams = {
            ...hashLocationParams,
            urlFragment: parsedLocationHash,
        };
    }

    return hashLocationParams;
}
